import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import urls from '@/urls';

type SalesEdgeIntegrationStatusType =
  | 'NULL'
  | 'NEW'
  | 'SCHEMA_SYNCED'
  | 'ACTIVE'
  | 'INVALID_CREDENTIALS'
  | 'INACTIVE'
  | 'UNAUTHORIZED_CREDENTIALS';

// Subset of the pyjack response. Full payload:
// {
//   integration: {
//     provider: 'CROSSBEAM',
//     status: 'ACTIVE',
//     last_sync_at: null,
//     partners_last_sync_at: null,
//     partners_unclassified_populations: null,
//     org_uuid: 'org_1_uuid',
//     can_change_org_id: false,
//     populations: [
//       { name: 'Prospects', id: '1', status: 'COLD', is_standard: true },
//       { name: 'Customers', id: '2', status: 'CLOSED:WON', is_standard: true },
//       { name: 'Open Opportunities', id: '3', status: 'PROSPECT', is_standard: true },
//     ],
//   } | null
// }
type SalesEdgeIntegrationResponse = {
  integration: {
    provider: string;
    status: Exclude<SalesEdgeIntegrationStatusType, 'NULL'>;
    last_sync_at: string | null;
  } | null;
};

export const useSalesEdgeIntegrationStore = defineStore(
  'SalesEdgeIntegration',
  () => {
    const { currentOrg } = useAuth();

    const status = ref<SalesEdgeIntegrationStatusType | null>(null);
    const lastSyncAt = ref<string | null>(null);
    const isLoading = ref(false);
    const hasError = ref(false);
    // The status endpoint is admin-only. A 403 means the current user lacks
    // permission to manage the integration, which is distinct from a genuine
    // failure (500/network) and should not read as "Status unknown".
    const hasNoAccess = ref(false);
    const hasReauthorizationInFlight = ref(false);

    let abortController: AbortController | null = null;

    async function fetchStatus() {
      const orgUuid = currentOrg.value?.uuid;
      if (!orgUuid) return;
      abortController?.abort();
      abortController = new AbortController();
      const { signal } = abortController;
      isLoading.value = true;
      hasError.value = false;
      hasNoAccess.value = false;
      try {
        const { data } = await axios.get<SalesEdgeIntegrationResponse>(
          urls.salesEdge.crossbeamIntegration(orgUuid),
          { signal },
        );
        if (signal.aborted) return;
        status.value = data?.integration?.status ?? 'NULL';
        lastSyncAt.value = data?.integration?.last_sync_at ?? null;
      } catch (e) {
        if (signal.aborted) return;
        status.value = null;
        lastSyncAt.value = null;
        if (axios.isAxiosError(e) && e.response?.status === 403) {
          hasNoAccess.value = true;
        } else {
          hasError.value = true;
        }
      } finally {
        if (!signal.aborted) isLoading.value = false;
      }
    }

    function flagReauthorizationInFlight() {
      hasReauthorizationInFlight.value = true;
    }

    function consumeReauthorizationInFlight() {
      if (!hasReauthorizationInFlight.value) return false;
      hasReauthorizationInFlight.value = false;
      return true;
    }

    const isActive = computed(() => status.value === 'ACTIVE');
    const isSetupIncomplete = computed(
      () => status.value !== null && !isActive.value,
    );

    return {
      status,
      lastSyncAt,
      isActive,
      isSetupIncomplete,
      isLoading,
      hasError,
      hasNoAccess,
      hasReauthorizationInFlight,
      fetchStatus,
      flagReauthorizationInFlight,
      consumeReauthorizationInFlight,
    };
  },
);
